import { Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { useCurrentUser, useLogout, useSwitchTenant } from '@hooks/useAuth';
import { initials } from '@lib/format';
import { brand } from '@/config/brand';

type NavbarProps = {
  onMenuToggle: () => void;
};

export function Navbar({ onMenuToggle }: NavbarProps) {
  const location = useLocation();
  const { data: currentUser } = useCurrentUser();
  const switchTenantMutation = useSwitchTenant();
  const logoutMutation = useLogout();
  const name = currentUser ? `${currentUser.first_name} ${currentUser.last_name}`.trim() : 'Founder';

  const getPageTitle = (pathname: string): string => {
    const routes: Record<string, string> = {
      '/': 'Dashboard',
      '/app-health': 'App Health',
      '/apollo': 'Apollo Prospecting',
      '/leads': 'Leads',
      '/accounts': 'Accounts',
      '/opportunities': 'Opportunities',
      '/cadence': 'Cadence',
      '/templates': 'Templates',
      '/settings': 'Settings',
    };
    return routes[pathname] ?? '';
  };

  return (
    <nav className="bg-surface-base/90 backdrop-blur-sm border-b border-border-default sticky top-0 z-40">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onMenuToggle}
            className="lg:hidden p-2 hover:bg-surface-raised rounded-lg transition-colors text-content-secondary hover:text-content-primary"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="hidden sm:flex items-center gap-2">
            <img
              src={brand.logoUrl}
              alt={`${brand.name} logo`}
              className="h-8 w-8 object-contain"
            />
            <span className="text-sm font-semibold text-content-secondary">
              {brand.name}
            </span>
          </div>

          <h1 className="text-xl font-bold text-content-primary">
            {getPageTitle(location.pathname)}
          </h1>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
        {currentUser?.available_tenants?.length ? (
          <label className="flex flex-col gap-1 text-xs text-content-muted">
            <span>Tenant</span>
            <SearchableSelect
              options={currentUser.available_tenants.map((tenant) => ({ id: tenant.id, label: tenant.name }))}
              value={currentUser.active_tenant_id ?? ''}
              onChange={(value) => switchTenantMutation.mutate(value)}
              disabled={switchTenantMutation.isPending}
              className="min-w-[210px]"
              placeholder="Select tenant"
              clearable={false}
              aria-label="Tenant"
            />
          </label>
        ) : null}

        <div className="flex items-center gap-3 rounded-lg border border-border-default bg-surface-base px-3 py-2 shadow-sm">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-semibold">
            {initials(name)}
          </div>
          <div>
            <strong className="block text-sm text-content-primary">{name}</strong>
            <p className="m-0 text-xs text-content-muted">{currentUser?.active_tenant?.name ?? 'No active tenant'}</p>
          </div>
        </div>

        <button
          type="button"
          className="inline-flex min-h-11 items-center justify-center rounded-lg border border-border-default bg-surface-raised px-4 text-sm font-medium text-content-primary transition-colors hover:bg-surface-overlay"
          onClick={() => logoutMutation.mutate()}
        >
          Log Out
        </button>
      </div>
      </div>
    </nav>
  );
}
